import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function parseSurfaceLabel(label: string): { type: string; index: number } {
  // Parse surface labels in multiple formats:
  // - Backend format: "floor", "wall_0", "wall_1", "ceiling"
  // - Legacy format: "floor", "wall1", "wall2", "ceiling"

  // Try backend format first: "wall_0", "wall_1", etc. (0-based index)
  const backendMatch = label.match(/^(floor|wall|ceiling|walls|stairs)_(\d+)$/i);
  if (backendMatch) {
    const type = backendMatch[1].toLowerCase();
    const index = parseInt(backendMatch[2], 10); // 0-based index from backend
    return { type: type === 'wall' ? 'walls' : type, index };
  }

  // Try legacy format: "wall1", "wall2", etc. (1-based index)
  const legacyMatch = label.match(/^(floor|wall|ceiling|walls)(\d*)$/i);
  if (legacyMatch) {
    const type = legacyMatch[1].toLowerCase();
    const index = legacyMatch[2] ? parseInt(legacyMatch[2], 10) - 1 : 0; // Convert 1-based to 0-based
    return { type: type === 'wall' ? 'walls' : type, index };
  }

  return { type: label.toLowerCase(), index: 0 };
}

export function formatSurfaceLabel(type: string, index: number): string {
  if (type === 'walls') {
    return `Wall ${index + 1}`;
  }
  if (type === 'stairs') {
    return `Stairs ${index + 1}`;
  }
  // Capitalize first letter
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function formatSurfaceLabelFromRaw(label: string): string {
  // Parse raw labels like "floor", "wall1", "wall2", "ceiling" into human-readable
  const { type, index } = parseSurfaceLabel(label);
  return formatSurfaceLabel(type, index);
}

export function base64ToImageUrl(base64: string, mimeType = 'image/png'): string {
  if (base64.startsWith('data:')) {
    return base64;
  }
  return `data:${mimeType};base64,${base64}`;
}

export async function imageUrlToBase64(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data URL prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
